import { priceSourcesApi } from 'app/services/api';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { extractErrorMessage } from '../marginCenter.actionUtils';
import { PRICE_SOURCE_VERSION_REVIEW_DECISION } from '../marginCenter.constants';

export const PRICE_SOURCES_ACTION_TYPE = createNamedActions('marginCenter/priceSources', {
  SET_LIST_LOADING: 'SET_LIST_LOADING',
  GET_LIST: 'GET_LIST',
  SET_LIST_ERROR: 'SET_LIST_ERROR',
  SET_CURRENT_LOADING: 'SET_CURRENT_LOADING',
  GET_CURRENT: 'GET_CURRENT',
  SET_CURRENT_ERROR: 'SET_CURRENT_ERROR',
  SET_VERSIONS_LOADING: 'SET_VERSIONS_LOADING',
  GET_VERSIONS: 'GET_VERSIONS',
  SET_VERSIONS_ERROR: 'SET_VERSIONS_ERROR',
  SET_BASE_VERSION_LOADING: 'SET_BASE_VERSION_LOADING',
  GET_BASE_VERSION: 'GET_BASE_VERSION',
  SET_BASE_VERSION_ERROR: 'SET_BASE_VERSION_ERROR',
  SET_SERVING_VERSION: 'SET_SERVING_VERSION',
  SET_SAVING: 'SET_SAVING',
  SET_SAVE_ERROR: 'SET_SAVE_ERROR',
  UPSERT_SOURCE: 'UPSERT_SOURCE',
  REMOVE_SOURCE: 'REMOVE_SOURCE',
  SET_VERSION_SAVING: 'SET_VERSION_SAVING',
  SET_VERSION_SAVE_ERROR: 'SET_VERSION_SAVE_ERROR',
  SET_VERSION_SAVE_RESULT: 'SET_VERSION_SAVE_RESULT',
  CLEAR_SOURCE_DETAIL: 'CLEAR_SOURCE_DETAIL',
  CLEAR_SAVE_ERROR: 'CLEAR_SAVE_ERROR',
  SET_REPUBLISHING: 'SET_REPUBLISHING',
  SET_REPUBLISH_ERROR: 'SET_REPUBLISH_ERROR',
  SET_REPUBLISH_RESULT: 'SET_REPUBLISH_RESULT',
  CLEAR_REPUBLISH: 'CLEAR_REPUBLISH',
});

const VERSION_HISTORY_TAKE = 50;

export const getPriceSourcesAction = () => async dispatch => {
  dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_LIST_LOADING, payload: true });

  try {
    const { data } = await priceSourcesApi.getSources();

    return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.GET_LIST, payload: data ?? [] });
  } catch {
    return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_LIST_ERROR });
  }
};

export const getPriceSourceAction = priceSourceId => async dispatch => {
  dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_CURRENT_LOADING, payload: true });

  try {
    const { data } = await priceSourcesApi.getSource(priceSourceId);

    return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.GET_CURRENT, payload: data });
  } catch {
    return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_CURRENT_ERROR });
  }
};

export const getPriceSourceVersionsAction = priceSourceId => async dispatch => {
  dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_VERSIONS_LOADING, payload: true });

  try {
    const { data } = await priceSourcesApi.getVersions(priceSourceId, {
      take: VERSION_HISTORY_TAKE,
    });

    return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.GET_VERSIONS, payload: data ?? [] });
  } catch {
    return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_VERSIONS_ERROR });
  }
};

// The grid editor prefills from the newest version of ANY status (not only the serving one) so a
// quarantined draft is never silently discarded; the payload is null when the source has no version.
export const getPriceSourceBaseVersionAction = priceSourceId => async dispatch => {
  dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_BASE_VERSION_LOADING, payload: true });

  try {
    const { data: summaries } = await priceSourcesApi.getVersions(priceSourceId, { take: 1 });
    const [latestSummary] = summaries ?? [];

    if (!latestSummary) {
      return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.GET_BASE_VERSION, payload: null });
    }

    const { data } = await priceSourcesApi.getVersion(priceSourceId, latestSummary.versionId);

    return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.GET_BASE_VERSION, payload: data });
  } catch {
    return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_BASE_VERSION_ERROR });
  }
};

// The adjustments card shows the serving version's parsed LLPA grids (its `adjustments`). The grid
// editor's base version already carries them when it is the serving version, so this read is only
// for a serving version that a newer, quarantined version sits on top of. A failed read shows no
// parsed grids rather than an error, because the card's manual grids stay editable either way.
export const getPriceSourceServingVersionAction =
  ({ priceSourceId, versionId }) =>
  async dispatch => {
    if (!versionId) {
      return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_SERVING_VERSION, payload: null });
    }

    try {
      const { data } = await priceSourcesApi.getVersion(priceSourceId, versionId);

      return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_SERVING_VERSION, payload: data });
    } catch {
      return dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_SERVING_VERSION, payload: null });
    }
  };

// The mapping card reads the candidates on its own, without the store: the source's endpoint works
// before the first upload, and the wizard keeps its own copy keyed to the open upload.
export const readPriceSourceProgramCandidatesAction = priceSourceId => async () => {
  try {
    const { data } = await priceSourcesApi.getProgramCandidates(priceSourceId);

    return data ?? [];
  } catch {
    return null;
  }
};

// The pricing workbench offers investor (Non-QM) fields only to a company that prices from a rate
// sheet. A failed read shows no investor fields.
export const readHasRateSheetSourceAction = () => async () => {
  try {
    const { data } = await priceSourcesApi.getRateSheetSourceAvailability();

    return !!data?.hasActiveRateSheetSource;
  } catch {
    return false;
  }
};

export const clearPriceSourceDetailAction = () => ({
  type: PRICE_SOURCES_ACTION_TYPE.CLEAR_SOURCE_DETAIL,
});

export const clearPriceSourceSaveErrorAction = () => ({
  type: PRICE_SOURCES_ACTION_TYPE.CLEAR_SAVE_ERROR,
});

export const savePriceSourceAction =
  ({ priceSourceId, request }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_SAVING, payload: true });

    try {
      const { data } = priceSourceId
        ? await priceSourcesApi.updateSource(priceSourceId, request)
        : await priceSourcesApi.createSource(request);

      dispatch({ type: PRICE_SOURCES_ACTION_TYPE.UPSERT_SOURCE, payload: data });

      return data;
    } catch (error) {
      dispatch({
        type: PRICE_SOURCES_ACTION_TYPE.SET_SAVE_ERROR,
        payload: extractErrorMessage(error),
      });

      return null;
    }
  };

export const deletePriceSourceAction = priceSourceId => async dispatch => {
  dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_SAVING, payload: true });

  try {
    await priceSourcesApi.deleteSource(priceSourceId);
    dispatch({ type: PRICE_SOURCES_ACTION_TYPE.REMOVE_SOURCE, payload: priceSourceId });

    return true;
  } catch (error) {
    dispatch({
      type: PRICE_SOURCES_ACTION_TYPE.SET_SAVE_ERROR,
      payload: extractErrorMessage(error),
    });

    return false;
  }
};

// The result is dispatched only after the refetches settle: it clears isSaving, and an earlier clear
// would let a second Save race the new base version into a 409.
// A gate outcome (Quarantined / HardReject) is a 200 — the new version is persisted with its tripped
// checks and the previous Quotable version keeps serving — so it lands in `result`, not an error. Only
// a 400 / 409 / transport failure reaches the error slice; a 409 means another save landed first.
export const createPriceSourceVersionAction =
  ({ priceSourceId, request }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_VERSION_SAVING, payload: true });

    try {
      const { data } = await priceSourcesApi.createVersion(priceSourceId, request);

      await Promise.all([
        dispatch(getPriceSourceAction(priceSourceId)),
        dispatch(getPriceSourceVersionsAction(priceSourceId)),
        dispatch(getPriceSourceBaseVersionAction(priceSourceId)),
      ]);

      dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_VERSION_SAVE_RESULT, payload: data });

      return data;
    } catch (error) {
      dispatch({
        type: PRICE_SOURCES_ACTION_TYPE.SET_VERSION_SAVE_ERROR,
        payload: {
          message: extractErrorMessage(error),
          isConflict: error?.response?.status === HTTP_RESPONSE_STATUS_CODES.CONFLICT,
        },
      });

      return null;
    }
  };

// Republishing copies the serving version's prices into a new version that snapshots the saved
// manual adjustment grids, so quotes start pricing with them. It runs the version checks; a 403 means
// the change needs a second person and the caller last edited the grids, and a 409 means another
// version landed after the page loaded (or there is no Quotable version to copy).
export const republishPriceSourceVersionAction =
  ({ priceSourceId, request }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_REPUBLISHING });

    try {
      const { data } = await priceSourcesApi.republishVersion(priceSourceId, request);

      await Promise.allSettled([
        dispatch(getPriceSourceAction(priceSourceId)),
        dispatch(getPriceSourceVersionsAction(priceSourceId)),
        dispatch(getPriceSourceBaseVersionAction(priceSourceId)),
      ]);

      dispatch({ type: PRICE_SOURCES_ACTION_TYPE.SET_REPUBLISH_RESULT, payload: data });

      return data;
    } catch (error) {
      dispatch({
        type: PRICE_SOURCES_ACTION_TYPE.SET_REPUBLISH_ERROR,
        payload: {
          message: extractErrorMessage(error),
          isConflict: error?.response?.status === HTTP_RESPONSE_STATUS_CODES.CONFLICT,
          isForbidden: error?.response?.status === HTTP_RESPONSE_STATUS_CODES.FORBIDDEN,
          conflictCode: error?.response?.data?.code ?? null,
        },
      });

      return null;
    }
  };

export const clearPriceSourceRepublishAction = () => ({
  type: PRICE_SOURCES_ACTION_TYPE.CLEAR_REPUBLISH,
});

// A person's review of a Quarantined version: promoting makes it Quotable and serving, discarding
// keeps it Quarantined so it never serves. The page re-reads the source and its versions after
// either. A 403 means the caller saved the version, and a 409 means it was already reviewed or a
// newer version landed; the caller shows those inline, so the outcome is returned, not stored.
export const reviewPriceSourceVersionAction =
  ({ priceSourceId, versionId, decision }) =>
  async dispatch => {
    try {
      const { data } =
        decision === PRICE_SOURCE_VERSION_REVIEW_DECISION.PROMOTED
          ? await priceSourcesApi.promoteVersion(priceSourceId, versionId)
          : await priceSourcesApi.discardVersion(priceSourceId, versionId);

      await Promise.allSettled([
        dispatch(getPriceSourceAction(priceSourceId)),
        dispatch(getPriceSourceVersionsAction(priceSourceId)),
        dispatch(getPriceSourceBaseVersionAction(priceSourceId)),
      ]);

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: {
          message: extractErrorMessage(error),
          status: error?.response?.status ?? null,
          conflictCode: error?.response?.data?.code ?? null,
        },
      };
    }
  };
